import { useState } from "react";
import { ArrowUpRight, Minus, Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REENGAGE_SHARE, WIN_BACK_RATE, calculate } from "./pricingEstimate";

const money = (value: number) => `S$${Math.round(value).toLocaleString("en-US")}`;
const toNumber = (value: string) => Math.max(0, Math.floor(Number(value.replace(/,/g, "")) || 0));

// Each range is estimated at its midpoint.
const REVENUE_RANGES = [
  { label: "Under S$10,000", value: "5000" },
  { label: "S$10,000 to S$25,000", value: "17500" },
  { label: "S$25,000 to S$50,000", value: "37500" },
  { label: "S$50,000 to S$100,000", value: "75000" },
  { label: "S$100,000 to S$250,000", value: "175000" },
  { label: "Over S$250,000", value: "300000" },
];

// `max` picks the plan that fits; `typical` drives the revenue estimate.
const CONTACT_RANGES = [
  { label: "Up to 1,000", value: "1000", max: 1000, typical: 500 },
  { label: "1,001 to 2,000", value: "2000", max: 2000, typical: 1500 },
  { label: "2,001 to 5,000", value: "5000", max: 5000, typical: 3500 },
  { label: "5,001 to 8,000", value: "8000", max: 8000, typical: 6500 },
  { label: "8,001 to 15,000", value: "15000", max: 15000, typical: 11500 },
  { label: "15,001 to 30,000", value: "30000", max: 30000, typical: 22500 },
  { label: "Over 30,000", value: "30001", max: 30001, typical: 40000 },
];

type NumberFieldProps = { id: string; label: string; value: string; onChange: (value: string) => void; step?: number; min?: number };

function NumberField({ id, label, value, onChange, step, min = 0 }: NumberFieldProps) {
  const nudge = (direction: 1 | -1) => onChange(String(Math.max(min, toNumber(value) + direction * (step ?? 1))));
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className={`dh-calc-number${step ? " has-stepper" : ""}`}>
        {step && <button type="button" className="dh-calc-step is-minus" aria-label={`Decrease ${label.toLowerCase()}`} onClick={() => nudge(-1)} disabled={toNumber(value) <= min}><Minus aria-hidden /></button>}
        <Input id={id} inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} />
        {step && <button type="button" className="dh-calc-step is-plus" aria-label={`Increase ${label.toLowerCase()}`} onClick={() => nudge(1)}><Plus aria-hidden /></button>}
      </div>
    </div>
  );
}

type Props = { billing: "monthly" | "yearly"; demoUrl: string };

export default function PricingCalculator({ billing: initialBilling, demoUrl }: Props) {
  const [billing, setBilling] = useState(initialBilling);
  const [contacts, setContacts] = useState("2000");
  const [branches, setBranches] = useState("1");
  const [staff, setStaff] = useState("0");
  const [spendPerCustomer, setSpendPerCustomer] = useState("80");
  const [monthlyRevenue, setMonthlyRevenue] = useState("37500");
  const [advanced, setAdvanced] = useState(false);
  const [bookings, setBookings] = useState(false);
  const [businessNumbers, setBusinessNumbers] = useState(false);

  const contactRange = CONTACT_RANGES.find((range) => range.value === contacts) ?? CONTACT_RANGES[1];
  const result = calculate({
    billing,
    contacts: contactRange.max,
    contactsTypical: contactRange.typical,
    branches: Math.max(1, toNumber(branches)),
    staff: toNumber(staff),
    advanced,
    bookings,
    businessNumbers,
    spendPerCustomer: toNumber(spendPerCustomer),
    monthlyRevenue: toNumber(monthlyRevenue),
  });
  const barMax = Math.max(result.recovered, result.totalCost ?? 0, 1);

  return (
    <div id="calculator" className="dh-calc">
      <h2 className="dh-addons-title">Estimate your return</h2>
      <div className="dh-calc-grid">
        <div className="dh-calc-card dh-calc-inputs">
          <h3>Your business</h3>
          <div className="dh-calc-fields">
            <div>
              <Label htmlFor="calc-contacts">Contacts in your database</Label>
              <Select value={contacts} onValueChange={setContacts}>
                <SelectTrigger id="calc-contacts" className="dh-calc-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTACT_RANGES.map(({ label, value }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="calc-revenue">Current monthly revenue</Label>
              <Select value={monthlyRevenue} onValueChange={setMonthlyRevenue}>
                <SelectTrigger id="calc-revenue" className="dh-calc-select"><SelectValue /></SelectTrigger>
                <SelectContent>{REVENUE_RANGES.map(({ label, value }) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <NumberField id="calc-branches" label="Branches" value={branches} onChange={setBranches} step={1} min={1} />
            <NumberField id="calc-staff" label="Staff accounts" value={staff} onChange={setStaff} step={1} />
            <NumberField id="calc-spend" label="Average spend per customer (S$)" value={spendPerCustomer} onChange={setSpendPerCustomer} step={5} />
            <div>
              <Label id="calc-cycle-label">Payment cycle</Label>
              <div className="dh-calc-cycle" role="group" aria-labelledby="calc-cycle-label">
                {(["monthly", "yearly"] as const).map((cycle) => (
                  <button key={cycle} type="button" aria-pressed={billing === cycle} className={billing === cycle ? "is-active" : ""} onClick={() => setBilling(cycle)}>
                    {cycle === "monthly" ? "Monthly" : "Yearly"}{cycle === "yearly" && <span>-20%</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="dh-calc-checks">
            {([["advanced", "Advanced workflows and broadcasts (3 credits each)", advanced, setAdvanced], ["bookings", "Dibs Bookings (S$30 / month)", bookings, setBookings], ["numbers", "Business numbers (S$20 / month)", businessNumbers, setBusinessNumbers]] as const).map(([id, label, checked, set]) => (
              <label key={id} htmlFor={`calc-${id}`}><Checkbox id={`calc-${id}`} className="dh-calc-checkbox" checked={checked} onCheckedChange={(v) => set(v === true)} />{label}</label>
            ))}
          </div>
        </div>

        <div className="dh-calc-card dh-calc-results" aria-live="polite">
          <h3>Your estimate</h3>
          {result.totalCost === null ? (
            <p className="dh-calc-custom">Your needs are beyond our standard plans. Talk to us and we will build a custom Enterprise plan.</p>
          ) : (
            <>
              <div className="dh-calc-plan"><span>Recommended plan</span><strong>{result.plan}</strong></div>
              <dl className="dh-calc-lines">
                <div><dt>{result.plan} plan ({billing})</dt><dd>{money(result.planPrice)}</dd></div>
                {result.extraPacks > 0 && <div><dt>Extra AI credits ({result.extraPacks} × 200)</dt><dd>{money(result.extraCreditsCost)}</dd></div>}
                <div><dt>Add-ons</dt><dd>{money(result.addOnsCost)}</dd></div>
                <div className="dh-calc-total"><dt>Total monthly investment</dt><dd>{money(result.totalCost)}</dd></div>
              </dl>
            </>
          )}
          <div className="dh-calc-bars">
            <div><span>Revenue recovered</span><div className="dh-calc-bar"><i style={{ width: `${(result.recovered / barMax) * 100}%` }} /></div><strong>{money(result.recovered)}</strong></div>
            {result.totalCost !== null && <div><span>Your Dibs investment</span><div className="dh-calc-bar"><i className="is-spend" style={{ width: `${(result.totalCost / barMax) * 100}%` }} /></div><strong>{money(result.totalCost)}</strong></div>}
          </div>
          <div className="dh-calc-summary">
            {result.net !== null && <div><span>Net gain / month</span><strong>{result.net >= 0 ? "+" : "-"}{money(Math.abs(result.net))}</strong></div>}
            {result.returnMultiple !== null && <div><span>Return on spend</span><strong>{result.returnMultiple.toFixed(1)}x</strong></div>}
            <div><span>Overall revenue increase</span><strong>+{(result.revenueIncrease * 100).toFixed(1)}%</strong></div>
          </div>
          <p className="dh-calc-note">{`Illustrative estimate. Assumes Dibs re-engages ${REENGAGE_SHARE * 100}% of your contacts each month and ${WIN_BACK_RATE * 100}% of them return. Actual results vary.`}</p>
          <a className="dh-calc-cta" href={demoUrl} target="_blank" rel="noopener noreferrer">Book a demo <ArrowUpRight aria-hidden /></a>
        </div>
      </div>
    </div>
  );
}
